import {
    buildReducer,
    UnifiedGroup,
    makeHubPersonaId,
    UnifiedGroupExportStatus,
    UnifiedGroupRestoreStatus,
} from '../../../../shared';
import {
    loadUserUnifiedGroupsCompletedAction,
    favoriteGroupCompletedAction,
    followGroupCompletedAction,
    getGroupDetailsCompletedAction,
    initGroupsHubWithPrefetchDataAction,
    leaveGroupCompletedAction,
    getGroupMembershipRequestsCompletedAction,
    restoreGroupCompletedAction,
    restorePartiallyRestoredGroupCompletedAction,
    unFollowGroupCompletedAction,
    unFavoriteGroupCompletedAction,
    renewGroupCompletedAction,
    joinGroupCompletedAction,
    renewGroupInExoCompletedAction,
    addFavoritePersonaCompletedAction,
    removeFavoritePersonaCompletedAction,
    getUnifiedGroupExportStatusCompletedAction,
    createExportUnifiedGroupTaskCompletedAction,
    handleSoftDeletedGroupCompletedAction,
} from '../../../../hub';
import {
    dismissCreateGroupDialogAction,
    deleteGroupCompletedAction,
} from '../../../../editors/actions';
import {
    lpcGroupMembershipRequestApprovedAction,
    lpcFollowGroupCompletedAction,
    lpcUnFollowGroupCompletedAction,
    lpcLeaveGroupCompletedAction,
    lpcPromoteGroupMemberCompletedAction,
    lpcRemoveGroupMemberCompletedAction,
    lpcDemoteGroupMemberCompletedAction,
    lpcDemoteSelfCompletedAction,
    lpcJoinGroupCompletedAction,
} from '../../..';
import type { UnifiedGroupsMap } from '../../../state/data/groupsHubDataState';
import { UnifiedGroupAccessType } from '../../../../service';

export const enum GroupActionType {
    Favorite = 0,
    Follow = 1,
}

export default buildReducer<{ [unifiedGroupId: string]: UnifiedGroup }>()
    .handle(deleteGroupCompletedAction, (state, action) => {
        return updateUnifiedGroupsMapOnDeleteGroup(state, action.payload.hubPersonaId);
    })
    .handle(handleSoftDeletedGroupCompletedAction, (state, action) => {
        return updateUnifiedGroupsMapOnDeleteGroup(state, action.payload.hubPersonaId);
    })
    .handle(dismissCreateGroupDialogAction, (state, action) => {
        const originalMap = { ...state };
        if (action.payload.group) {
            originalMap[action.payload.group.hubPersonaId] = action.payload.group;
        }

        return originalMap;
    })
    .handleDataAction(favoriteGroupCompletedAction, (state, action) => {
        return updatePropertyInUnifiedGroupsMap(
            state,
            action.payload.hubPersonaId,
            GroupActionType.Favorite,
            true
        );
    })
    .handleDataAction(addFavoritePersonaCompletedAction, (state, action) => {
        if (state[action.payload.hubPersonaId]) {
            return updatePropertyInUnifiedGroupsMap(
                state,
                action.payload.hubPersonaId,
                GroupActionType.Favorite,
                true
            );
        }
        return state;
    })
    .handleDataAction(followGroupCompletedAction, (state, action) => {
        return updatePropertyInUnifiedGroupsMap(
            state,
            action.payload.hubPersonaId,
            GroupActionType.Follow,
            true
        );
    })
    .handleDataAction(getGroupDetailsCompletedAction, (originalMap, action) => {
        const updatedGroupsMap = { ...originalMap };
        const group = action.payload.group;

        if (!group) {
            return originalMap;
        }

        const hubPersonaIdWithId = makeHubPersonaId(group.externalDirectoryObjectId);
        const key =
            !originalMap[group.hubPersonaId] && originalMap[hubPersonaIdWithId]
                ? hubPersonaIdWithId
                : group.hubPersonaId;
        const oldGroupData: UnifiedGroup = updatedGroupsMap[key];
        if (oldGroupData) {
            updatedGroupsMap[key] = {
                ...group,
                isFavorite: oldGroupData.isFavorite,
                pendingMembershipRequests: oldGroupData.pendingMembershipRequests,
                isLoading: false,
                relevancyScore: oldGroupData.relevancyScore,
                lastVisitedTime: oldGroupData.lastVisitedTime,
                whenSoftDeletedTime: oldGroupData.whenSoftDeletedTime,
                joinedDateTime: oldGroupData.joinedDateTime,
                hubPersonaId: key,
                exportStatus: oldGroupData.exportStatus,
                exportDownloadLink: oldGroupData.exportDownloadLink,
                routingKey: oldGroupData.routingKey,
            };
        } else {
            updatedGroupsMap[key] = group;
        }

        return updatedGroupsMap;
    })
    .handleDataAction(getGroupMembershipRequestsCompletedAction, (state, action) => {
        const groupsMap = { ...state };
        if (groupsMap[action.payload.hubPersonaId]) {
            groupsMap[action.payload.hubPersonaId] = {
                ...groupsMap[action.payload.hubPersonaId],
                pendingMembershipRequests: action.payload.membershipRequestsCount,
            };
        }

        return groupsMap;
    })
    .handleDataAction(initGroupsHubWithPrefetchDataAction, (state, action) => {
        return updateUnifiedGroupMap(state, action.payload.unifiedGroups);
    })
    .handleDataAction(leaveGroupCompletedAction, (state, action) => {
        return updateLeaveAction({ ...state }, action.payload.hubPersonaId);
    })
    .handleDataAction(loadUserUnifiedGroupsCompletedAction, (state, action) => {
        return updateUnifiedGroupMap(state, action.payload.unifiedGroups);
    })
    .handle(lpcFollowGroupCompletedAction, (state, action) => {
        return updatePropertyInUnifiedGroupsMap(
            state,
            action.payload.hubPersonaId,
            GroupActionType.Follow,
            true
        );
    })

    .handle(lpcUnFollowGroupCompletedAction, (state, action) => {
        return updatePropertyInUnifiedGroupsMap(
            state,
            action.payload.hubPersonaId,
            GroupActionType.Follow,
            false
        );
    })
    .handle(lpcGroupMembershipRequestApprovedAction, (state, action) => {
        const originalMap = { ...state };
        if (originalMap[action.payload.hubPersonaId]) {
            const successCount =
                originalMap[action.payload.hubPersonaId].pendingMembershipRequests -
                (action.payload.membershipRequestsCount +
                    action.payload.failedMembershipRequestsCount);
            const modifiedGroup = {
                ...originalMap[action.payload.hubPersonaId],
                pendingMembershipRequests: action.payload.membershipRequestsCount,
                memberCount: originalMap[action.payload.hubPersonaId].memberCount + successCount,
                isLoading:
                    action.payload.membershipRequestsCount === 0
                        ? true
                        : originalMap[action.payload.hubPersonaId].isLoading,
            };

            originalMap[action.payload.hubPersonaId] = modifiedGroup;
        }

        return originalMap;
    })
    .handle(lpcLeaveGroupCompletedAction, (state, action) => {
        return updateLeaveAction({ ...state }, action.payload.hubPersonaId);
    })
    .handleDataAction(
        getUnifiedGroupExportStatusCompletedAction,
        (state, action) => {
            return updateUnifiedGroupsMapOnExportGroup(
                state,
                action.payload.hubPersonaId,
                action.payload.status,
                action.payload.link
            );
        },
        (state, action) => {
            return updateUnifiedGroupsMapOnExportGroup(
                state,
                action.payload.hubPersonaId,
                UnifiedGroupExportStatus.Failed,
                undefined
            );
        }
    )
    .handleDataAction(
        createExportUnifiedGroupTaskCompletedAction,
        (state, action) => {
            return updateUnifiedGroupsMapOnExportGroup(
                state,
                action.payload.hubPersonaId,
                action.payload.status,
                action.payload.link
            );
        },
        (state, action) => {
            return updateUnifiedGroupsMapOnExportGroup(
                state,
                action.payload.hubPersonaId,
                UnifiedGroupExportStatus.Failed,
                undefined
            );
        }
    )
    .handleDataAction(joinGroupCompletedAction, (state, action) => {
        const originalMap = { ...state };
        if (!originalMap[action.payload.group.hubPersonaId]) {
            originalMap[action.payload.group.hubPersonaId] = action.payload.group;
        }

        return updateUnifiedGroupMapAfterJoinAction(
            action.payload.group.accessType,
            action.payload.group.hubPersonaId,
            originalMap
        );
    })
    .handle(lpcJoinGroupCompletedAction, (state, action) => {
        const originalMap = { ...state };
        if (originalMap[action.payload.hubPersonaId]) {
            return updateUnifiedGroupMapAfterJoinAction(
                action.payload.accessType,
                action.payload.hubPersonaId,
                originalMap
            );
        }

        return originalMap;
    })
    .handle(lpcPromoteGroupMemberCompletedAction, (state, action) => {
        const originalMap = { ...state };
        if (originalMap[action.payload.hubPersonaId]) {
            let modifiedGroup: UnifiedGroup = originalMap[action.payload.hubPersonaId];
            modifiedGroup = {
                ...modifiedGroup,
                ownerCount: modifiedGroup.ownerCount + action.payload.promotedMembers,
            };

            originalMap[action.payload.hubPersonaId] = modifiedGroup;
        }
        return originalMap;
    })
    .handle(lpcRemoveGroupMemberCompletedAction, (state, action) => {
        const originalMap = { ...state };
        if (originalMap[action.payload.hubPersonaId]) {
            let modifiedGroup: UnifiedGroup = originalMap[action.payload.hubPersonaId];
            modifiedGroup = {
                ...modifiedGroup,
                memberCount: modifiedGroup.memberCount - action.payload.removedMembers,
            };
            originalMap[action.payload.hubPersonaId] = modifiedGroup;
        }
        return originalMap;
    })
    .handle(lpcDemoteGroupMemberCompletedAction, (state, action) => {
        const originalMap = { ...state };
        if (originalMap[action.payload.hubPersonaId]) {
            let modifiedGroup: UnifiedGroup = originalMap[action.payload.hubPersonaId];
            modifiedGroup = {
                ...modifiedGroup,
                ownerCount: modifiedGroup.ownerCount - action.payload.demotedMembers,
            };
            originalMap[action.payload.hubPersonaId] = modifiedGroup;
        }
        return originalMap;
    })
    .handle(lpcDemoteSelfCompletedAction, (state, action) => {
        const originalMap = { ...state };
        if (originalMap[action.payload.hubPersonaId]) {
            let modifiedGroup: UnifiedGroup = originalMap[action.payload.hubPersonaId];
            modifiedGroup = {
                ...modifiedGroup,
                isOwner: false,
            };
            originalMap[action.payload.hubPersonaId] = modifiedGroup;
        }
        return originalMap;
    })
    .handle(restorePartiallyRestoredGroupCompletedAction, (state, action) => {
        return updateMapOnRestoreGroup(state, action.payload.hubPersonaId);
    })
    .handleDataAction(
        restoreGroupCompletedAction,
        (state, action) => {
            return updateMapOnRestoreGroup(state, action.payload.hubPersonaId);
        },
        (state, action) => {
            const originalMap = { ...state };
            if (originalMap[action.payload.hubPersonaId]) {
                const modifiedGroup = {
                    ...originalMap[action.payload.hubPersonaId],
                    restoreStatus: action.payload.errorType,
                };

                originalMap[action.payload.hubPersonaId] = modifiedGroup;
            }

            return originalMap;
        }
    )
    .handleDataAction(unFavoriteGroupCompletedAction, (state, action) => {
        return updatePropertyInUnifiedGroupsMap(
            state,
            action.payload.hubPersonaId,
            GroupActionType.Favorite,
            false
        );
    })
    .handleDataAction(removeFavoritePersonaCompletedAction, (state, action) => {
        if (state[action.payload.hubPersonaId]) {
            return updatePropertyInUnifiedGroupsMap(
                state,
                action.payload.hubPersonaId,
                GroupActionType.Favorite,
                false
            );
        }
        return state;
    })
    .handleDataAction(unFollowGroupCompletedAction, (state, action) => {
        return updatePropertyInUnifiedGroupsMap(
            state,
            action.payload.hubPersonaId,
            GroupActionType.Follow,
            false
        );
    })
    .handleDataAction(renewGroupCompletedAction, (state, action) => {
        return updateIsExpiring({ ...state }, action.payload.hubPersonaId);
    })
    .handleDataAction(renewGroupInExoCompletedAction, (state, action) => {
        return updateIsExpiring({ ...state }, action.payload.hubPersonaId);
    })
    .done({});

function updatePropertyInUnifiedGroupsMap(
    originalMap: { [hubPersonaId: string]: UnifiedGroup },
    hubPersonaId: string,
    actionType: GroupActionType,
    newState: boolean
): { [hubPersonaId: string]: UnifiedGroup } {
    if (originalMap[hubPersonaId]) {
        const updatedGroupsMap = { ...originalMap };
        let modifiedGroup: UnifiedGroup = updatedGroupsMap[hubPersonaId];

        switch (actionType) {
            case GroupActionType.Favorite:
                modifiedGroup = {
                    ...modifiedGroup,
                    isFavorite: newState,
                };
                break;
            case GroupActionType.Follow:
                modifiedGroup = {
                    ...modifiedGroup,
                    isFollowed: newState,
                };
                break;
            default:
                break;
        }

        updatedGroupsMap[hubPersonaId] = modifiedGroup;
        return updatedGroupsMap;
    }

    return originalMap;
}

function updateMapOnRestoreGroup(state, payloadGroupId): { [hubPersonaId: string]: UnifiedGroup } {
    const originalMap = { ...state };
    const group = originalMap[payloadGroupId];
    const hubPersonaId = makeHubPersonaId(group.emailAddress);

    if (originalMap[payloadGroupId]) {
        delete originalMap[payloadGroupId];
    }

    originalMap[hubPersonaId] = {
        ...group,
        whenSoftDeletedTime: null,
        isLoading: true,
        hubPersonaId: hubPersonaId,
        restoreStatus: UnifiedGroupRestoreStatus.SUCCESSFUL,
    };

    return originalMap;
}

function updateUnifiedGroupMap(
    originalMap: { [hubPersonaId: string]: UnifiedGroup },
    groups: UnifiedGroup[]
): { [hubPersonaId: string]: UnifiedGroup } {
    const updatedGroupsMap = { ...originalMap };
    if (groups) {
        groups.forEach(group => {
            const hubPersonaId = group.hubPersonaId;

            if (updatedGroupsMap[hubPersonaId]) {
                const oldGroupData = updatedGroupsMap[hubPersonaId];
                updatedGroupsMap[hubPersonaId] = {
                    ...group,
                    isFollowed: oldGroupData.isFollowed,
                    pendingMembershipRequests: oldGroupData.pendingMembershipRequests,
                    isLoading: oldGroupData.isLoading,
                    isSubscriptionEnabled: oldGroupData.isSubscriptionEnabled,
                    exportStatus: oldGroupData.exportStatus,
                    exportDownloadLink: oldGroupData.exportDownloadLink,
                };
            } else {
                updatedGroupsMap[hubPersonaId] = group;
            }
        });
    }

    return updatedGroupsMap;
}

function updateIsExpiring(originalMap: UnifiedGroupsMap, hubPersonaId: string): UnifiedGroupsMap {
    if (originalMap[hubPersonaId]) {
        originalMap[hubPersonaId] = {
            ...originalMap[hubPersonaId],
            isExpiring: false,
        };
    }

    return originalMap;
}

function updateUnifiedGroupMapAfterJoinAction(
    groupAccessType: UnifiedGroupAccessType,
    hubPersonaId: string,
    originalMap: UnifiedGroupsMap
): UnifiedGroupsMap {
    if (groupAccessType === UnifiedGroupAccessType.Public) {
        originalMap[hubPersonaId] = {
            ...originalMap[hubPersonaId],
            isMember: true,
            memberCount: originalMap[hubPersonaId].memberCount + 1,
            joinedDateTime: new Date(),
        };
    } else {
        originalMap[hubPersonaId] = {
            ...originalMap[hubPersonaId],
            isJoinRequestPendingApproval: true,
        };
    }
    return originalMap;
}

function updateLeaveAction(originalMap: UnifiedGroupsMap, hubPersonaId: string): UnifiedGroupsMap {
    const group = originalMap[hubPersonaId];

    if (group) {
        originalMap[hubPersonaId] = {
            ...group,
            isMember: false,
            isOwner: false,
            memberCount: group.isMember ? group.memberCount - 1 : group.memberCount,
            ownerCount: group.isOwner ? group.ownerCount - 1 : group.ownerCount,
        };
    }
    return originalMap;
}
function updateUnifiedGroupsMapOnExportGroup(
    state: UnifiedGroupsMap,
    hubPersonaId: string,
    status: UnifiedGroupExportStatus,
    link: string
): UnifiedGroupsMap {
    const originalMap = { ...state };

    const oldGroupData: UnifiedGroup = originalMap[hubPersonaId];
    if (oldGroupData) {
        originalMap[hubPersonaId] = {
            ...oldGroupData,
            exportStatus: status,
            exportDownloadLink: link,
        };
    }
    return originalMap;
}

function updateUnifiedGroupsMapOnDeleteGroup(
    state: UnifiedGroupsMap,
    currentHubPersonaId: string
): UnifiedGroupsMap {
    const originalMap = { ...state };
    const group = originalMap[currentHubPersonaId];
    if (group) {
        const hubPersonaId = makeHubPersonaId(group.externalDirectoryObjectId || group.mailboxGuid);

        delete originalMap[currentHubPersonaId];

        originalMap[hubPersonaId] = {
            ...group,
            hubPersonaId: hubPersonaId,
            whenSoftDeletedTime: new Date(),
            isFavorite: false,
        };
    }

    return originalMap;
}
export type { Action } from '../../../../shared';
